import React, { useMemo } from "react";
import {
  Box,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Visibility,
  Edit,
  Delete,
  Download,
} from "@mui/icons-material";
import {
  DEFAULT_PAGE_SIZE,
  PAGINATION_PAGE_SIZE_OPTIONS,
} from "../../config/pagination";

/**
 * Reusable DataTable component with configurable columns, actions, and features
 * 
 * @param {Array} rows - Array of data objects
 * @param {Array} columns - Column definitions (can include custom renderCell)
 * @param {boolean} loading - Loading state
 * @param {Array} actions - Array of action types: 'view', 'edit', 'delete', 'custom'
 * @param {Function} onAction - Callback when action is clicked: (action, row) => void
 * @param {Function} onRowClick - Callback when row is clicked: (row) => void
 * @param {boolean} pagination - Enable pagination (default: true)
 * @param {boolean} searchable - Enable search (default: false)
 * @param {boolean} exportable - Enable export (default: false)
 * @param {boolean} selectable - Enable row selection (default: false)
 * @param {Function} onSelectionChange - Callback when selection changes
 * @param {Object} initialState - Initial DataGrid state
 * @param {Object} sx - Custom styles
 * @param {ReactNode} toolbar - Custom toolbar component
 */
const DataTable = ({
  rows = [],
  columns = [],
  loading = false,
  actions = [],
  onAction,
  onRowClick,
  pagination = true,
  searchable = false,
  exportable = false,
  selectable = false,
  onSelectionChange,
  initialState,
  sx = {},
  toolbar,
  pageSizeOptions = PAGINATION_PAGE_SIZE_OPTIONS,
  ...otherProps
}) => {
  // Map action types to icons and labels
  const actionConfig = {
    view: { icon: <Visibility />, label: "View", color: "primary" },
    edit: { icon: <Edit />, label: "Edit", color: "primary" },
    delete: { icon: <Delete />, label: "Delete", color: "error" },
  };

  // Add actions column if actions are specified
  const columnsWithActions = useMemo(() => {
    if (actions.length === 0) return columns;

    const actionsColumn = {
      field: "actions",
      headerName: "Actions",
      width: actions.length * 50 + 20,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          {actions.map((action, index) => {
            if (typeof action === "string") {
              const config = actionConfig[action];
              if (!config) return null;

              return (
                <Tooltip key={index} title={config.label}>
                  <IconButton
                    size="small"
                    color={config.color}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction?.(action, params.row);
                    }}
                  >
                    {config.icon}
                  </IconButton>
                </Tooltip>
              );
            } else if (typeof action === "object" && action.render) {
              // Custom action
              return (
                <Box key={index} onClick={(e) => e.stopPropagation()}>
                  {action.render(params.row, (actionType) =>
                    onAction?.(actionType, params.row)
                  )}
                </Box>
              );
            }
            return null;
          })}
        </Box>
      ),
    };

    return [...columns, actionsColumn];
  }, [columns, actions, onAction]);

  const defaultInitialState = {
    pagination: { paginationModel: { pageSize: DEFAULT_PAGE_SIZE } },
    ...initialState,
  };

  return (
    <Box sx={{ width: "100%", ...sx }}>
      {toolbar && <Box sx={{ mb: 2 }}>{toolbar}</Box>}
      <DataGrid
        rows={rows}
        columns={columnsWithActions}
        loading={loading}
        onRowClick={onRowClick ? (params) => onRowClick(params.row) : undefined}
        checkboxSelection={selectable}
        onRowSelectionModelChange={onSelectionChange}
        pagination={pagination}
        pageSizeOptions={pageSizeOptions}
        initialState={defaultInitialState}
        disableRowSelectionOnClick={!selectable}
        sx={{
          border: "none",
          "& .MuiDataGrid-cell": { borderBottom: "1px solid #f0f0f0" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f5f5f5",
            fontWeight: 600,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#fafafa",
            cursor: onRowClick ? "pointer" : "default",
          },
        }}
        {...otherProps}
      />
    </Box>
  );
};

export default DataTable;

